import { memo, useCallback, useMemo, useRef } from 'react'
import { useInView } from 'react-intersection-observer'

import { Nullable } from '@types'

import Tabs from '@mui/material/Tabs'
import { isElementInView } from '@shared/utils'

import DroppableTabArea from './DroppableTabArea'
import {
  LeftButton,
  LeftIconTriangle,
  RightButton,
  RightIconTriangle,
  TabBadge,
  TabContent,
  TabItem,
  TabLabel,
  TabsCarouselWrapper,
  TabsContainer,
} from './index.styles'

export interface CarouselTabsProps {
  tabs: { id: number; label: string; disabled?: boolean; key?: string }[]
  value?: number | boolean
  onChange: (t: number) => void
  acceptDrops?: string
  handleDrop?: (tabIdFrom: number, tadIdTo: number, droppedItem: unknown, dropTarget: Nullable<HTMLElement>) => void
  badgeContent?: string
  checkIsBadgeShouldBeDisplayed?: (key: string) => boolean
}

const TabsCarousel = ({
  tabs,
  value = false,
  onChange,
  handleDrop,
  acceptDrops,
  badgeContent,
  checkIsBadgeShouldBeDisplayed,
}: CarouselTabsProps) => {
  const { ref: lastTabRef, inView: isLastTabVisible } = useInView({ threshold: 0.95 })
  const { ref: firstTabRef, inView: isFirstTabVisible } = useInView({ threshold: 0.95 })
  const tabRefs = useRef<Nullable<HTMLDivElement>[]>([])
  const containerRef = useRef<Nullable<HTMLDivElement>>(null)

  const handleChange = useCallback(
    (_: unknown, newValue: number) => {
      handleTabFocus(tabs.findIndex(tab => tab.id === newValue))
      onChange(newValue)
    },
    [tabs, onChange],
  )

  const handleScroll = (step: number) => {
    let newIndex = step < 0 ? 0 : tabs.length - 1
    let isCurrentTabVisible = isElementInView(
      tabRefs.current[newIndex]?.getBoundingClientRect(),
      containerRef.current?.getBoundingClientRect(),
    )

    while (!isCurrentTabVisible && newIndex >= 0 && newIndex < tabs.length) {
      newIndex -= step
      isCurrentTabVisible = isElementInView(
        tabRefs.current[newIndex]?.getBoundingClientRect(),
        containerRef.current?.getBoundingClientRect(),
      )
    }

    handleTabFocus(newIndex + step)
  }

  const handleScrollLeft = () => handleScroll(-1)
  const handleScrollRight = () => handleScroll(1)

  const handleTabFocus = (tabIndexToFocus: number) => {
    const targetTab = tabRefs.current[tabIndexToFocus]
    if (!targetTab) {
      return
    }
    const isLastTab = tabIndexToFocus === tabRefs.current.length - 1
    const isFirstTab = tabIndexToFocus === 0

    // eslint-disable-next-line no-nested-ternary
    const inline = isLastTab ? 'start' : isFirstTab ? 'end' : 'nearest'

    targetTab?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline })
  }

  const activeTabValue = useMemo(() => {
    const activeTab = tabs.find(tab => tab.id === value)
    return activeTab?.id ?? false
  }, [tabs, value])

  return (
    <TabsCarouselWrapper>
      {!isFirstTabVisible && (
        <LeftButton onClick={handleScrollLeft}>
          <LeftIconTriangle />
        </LeftButton>
      )}

      <TabsContainer ref={containerRef}>
        <Tabs value={activeTabValue} onChange={handleChange} variant="scrollable" scrollButtons={false}>
          {tabs.map((tabData, index) => (
            <TabItem
              $isSelected={value === tabData.id}
              key={tabData.id}
              value={tabData.id}
              sx={{ maxWidth: 'none', minWidth: '35px', fontWeight: '700' }}
              label={
                // eslint-disable-next-line no-nested-ternary
                value !== tabData.id && acceptDrops ? (
                  <DroppableTabArea
                    activeTabId={Number(value)}
                    tabId={tabData.id}
                    label={tabData.label}
                    acceptItemType={acceptDrops}
                    handleDrop={handleDrop}
                  />
                ) : badgeContent && checkIsBadgeShouldBeDisplayed?.(tabData?.key || '') ? (
                  <TabContent>
                    <TabLabel>{tabData.label}</TabLabel>
                    <TabBadge>{badgeContent}</TabBadge>
                  </TabContent>
                ) : (
                  tabData.label
                )
              }
              $isDisabled={tabData.disabled}
              ref={(el: HTMLDivElement) => {
                tabRefs.current[index] = el
                if (index === tabs.length - 1) {
                  lastTabRef(el)
                }
                if (index === 0) {
                  firstTabRef(el)
                }
              }}
            />
          ))}
        </Tabs>
      </TabsContainer>

      {!!tabs.length && !isLastTabVisible && (
        <RightButton onClick={handleScrollRight}>
          <RightIconTriangle />
        </RightButton>
      )}
    </TabsCarouselWrapper>
  )
}

export default memo(TabsCarousel)
